import { useState } from 'react';
import { X, Plus, Trash2, Zap } from 'lucide-react';
import { useMacros } from '@/lib/macros';

const STATES = ['', 'Triage', 'In Progress', 'On Hold', 'Resolved', 'Closed'];

export function MacroManager({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { macros, add, remove } = useMacros();
  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const [setStatus, setSetStatus] = useState('');

  if (!open) return null;

  const submit = () => {
    if (!name.trim() || !body.trim()) return;
    add({ name: name.trim(), body: body.trim(), setStatus: setStatus || undefined });
    setName('');
    setBody('');
    setSetStatus('');
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center p-6 pt-[8vh]">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div className="flex items-center gap-2 text-base font-semibold"><Zap className="h-4 w-4 text-primary" /> Macros</div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>
        <div className="max-h-[60vh] space-y-3 overflow-y-auto p-5">
          {macros.map((m) => (
            <div key={m.id} className="rounded-lg border border-border p-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{m.name}</span>
                {m.setStatus && <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">→ {m.setStatus}</span>}
                {!m.custom && <span className="text-[10px] text-muted-foreground">built-in</span>}
                {m.custom && (
                  <button onClick={() => remove(m.id)} className="ml-auto text-muted-foreground hover:text-temp-ember"><Trash2 className="h-3.5 w-3.5" /></button>
                )}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{m.body}</p>
            </div>
          ))}

          <div className="space-y-2 rounded-xl border border-dashed border-border p-3">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Macro name" className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm outline-none focus:border-primary" />
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={2} placeholder="Reply text — use {ref}, {requester}, {assignee}" className="w-full resize-y rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                Also set status
                <select value={setStatus} onChange={(e) => setSetStatus(e.target.value)} className="h-8 rounded-md border border-input bg-background px-2 text-sm outline-none focus:border-primary">
                  {STATES.map((s) => <option key={s} value={s}>{s || '— none —'}</option>)}
                </select>
              </label>
              <button onClick={submit} disabled={!name.trim() || !body.trim()} className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition hover:opacity-90 disabled:opacity-50">
                <Plus className="h-4 w-4" /> Add macro
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
